import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Checkbox } from "../components/ui/checkbox";
import {
  ArrowLeft, ExternalLink, Trophy, Calendar, Flame, Upload,
  CheckCircle2, FileText, Video as VideoIcon, Image as ImageIcon,
  Loader2, Trash2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const CHUNK_SIZE = 512 * 1024; // 512 KB per chunk — safely under proxy limits
const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB
const ALLOWED_MIME_RE = /^(image\/|video\/)|^application\/pdf$/;

export default function ChallengeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [challenge, setChallenge] = useState(null);
  const [busyStep, setBusyStep] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // 0-100
  const [caption, setCaption] = useState("");
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/challenges/${id}`);
      setChallenge(data);
    } catch {
      toast.error("Couldn't load challenge");
      navigate("/challenges");
    }
  };

  useEffect(() => { load(); }, [id]);

  const joined = !!challenge?.user_progress;
  const completed = !!challenge?.user_progress?.completed;
  const doneSteps = useMemo(
    () => new Set(challenge?.user_progress?.done_steps || []),
    [challenge],
  );
  const submissions = challenge?.user_progress?.submissions || [];
  const totalSteps = challenge?.steps?.length || 0;
  const stepsComplete = totalSteps > 0 && doneSteps.size === totalSteps;
  const progressPct = totalSteps === 0 ? 0 : Math.round((doneSteps.size / totalSteps) * 100);

  const handleJoin = async () => {
    try {
      await api.post(`/challenges/${id}/join`);
      toast.success("You're in!");
      await load();
    } catch { toast.error("Couldn't join"); }
  };

  const toggleStep = async (stepIdx, nextDone) => {
    if (!joined) {
      toast.error("Join the challenge first");
      return;
    }
    if (completed || busyStep !== null) return;
    setBusyStep(stepIdx);
    try {
      await api.post(`/challenges/${id}/step-toggle`, {
        step_index: stepIdx,
        done: nextDone,
      });
      await load();
    } catch { toast.error("Couldn't update step"); }
    finally { setBusyStep(null); }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    if (!ALLOWED_MIME_RE.test(file.type)) {
      toast.error("Only images, PDFs and short videos are allowed");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      toast.error("File too large (max 50MB)");
      return;
    }
    if (!joined) {
      toast.error("Join the challenge first");
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
      const initRes = await api.post(`/challenges/${id}/proof/upload-init`, {
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type,
        total_chunks: totalChunks,
      });
      const uploadId = initRes.data.upload_id;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const blob = file.slice(start, end);
        const form = new FormData();
        form.append("upload_id", uploadId);
        form.append("chunk_index", String(i));
        form.append("chunk", blob, `chunk-${i}`);
        await api.post(`/challenges/${id}/proof/upload-chunk`, form, {
          headers: { "Content-Type": undefined },
        });
        setProgress(Math.round(((i + 1) / totalChunks) * 100));
      }

      const finalForm = new FormData();
      finalForm.append("upload_id", uploadId);
      finalForm.append("caption", caption);
      const done = await api.post(`/challenges/${id}/proof/upload-complete`, finalForm, {
        headers: { "Content-Type": undefined },
      });
      const points = done.data?.points_earned || 0;
      if (points > 0) {
        toast.success(`Challenge complete! +${points} XP`);
        if (done.data.leveled_up) {
          setTimeout(() => toast.success(`Level up to ${done.data.user.level}!`), 700);
        }
      } else {
        toast.success("Proof uploaded");
      }
      setCaption("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await refresh();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const deleteSubmission = async (submissionId) => {
    try {
      await api.delete(`/challenges/${id}/proof/${submissionId}`);
      toast("Deleted");
      await load();
    } catch { toast.error("Couldn't delete"); }
  };

  if (!challenge) return <div className="text-white/60">Loading...</div>;

  const sponsored = !!challenge.sponsor_name;

  return (
    <div data-testid="challenge-detail-page" className="max-w-3xl">
      <Link to="/challenges" className="text-xs text-white/50 hover:text-white flex items-center gap-1">
        <ArrowLeft size={12}/> Back to challenges
      </Link>

      <div className={`glass-card p-7 md:p-9 mt-3 ${sponsored ? "border-neonyellow/30 shadow-[0_0_24px_rgba(255,215,0,0.15)]" : ""}`}>
        {sponsored && (
          <div className="flex items-center gap-2 mb-4 text-xs" data-testid="challenge-sponsor-badge">
            <span className="text-xs uppercase tracking-widest text-neonyellow font-bold">★ Sponsored</span>
            <span className="text-white/50">by</span>
            {challenge.sponsor_logo
              ? <img src={challenge.sponsor_logo} alt={challenge.sponsor_name} className="h-6 max-w-[120px] object-contain"/>
              : <span className="font-bold text-neonyellow">{challenge.sponsor_name}</span>}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="px-3 py-1 rounded-full bg-neonyellow/15 text-neonyellow font-bold flex items-center gap-1">
            <Trophy size={12}/> +{challenge.reward_points} XP
          </span>
          <span className="px-3 py-1 rounded-full bg-white/8 text-white/70 flex items-center gap-1">
            <Calendar size={12}/> {challenge.duration_days} days
          </span>
          {completed && (
            <span className="px-3 py-1 rounded-full bg-neongreen/20 text-neongreen font-bold flex items-center gap-1" data-testid="challenge-status-completed">
              <CheckCircle2 size={12}/> Completed
            </span>
          )}
        </div>

        <div className="flex items-start gap-4 mt-4">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${sponsored ? "bg-neonyellow/15 text-neonyellow" : "bg-neonpink/15 text-neonpink"}`}>
            <Flame size={26}/>
          </div>
          <div className="flex-1">
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">{challenge.title}</h1>
            <p className="text-white/70 mt-3 leading-relaxed">{challenge.description}</p>
          </div>
        </div>

        {!joined && (
          <button
            data-testid="challenge-join-button"
            onClick={handleJoin}
            className="btn-primary-neon mt-6 w-full sm:w-auto">
            Join challenge
          </button>
        )}

        {joined && totalSteps > 0 && (
          <>
            <div className="mt-7 flex items-center gap-3" data-testid="challenge-progress">
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-neoncyan to-neongreen transition-all duration-300"
                     style={{ width: `${progressPct}%` }}/>
              </div>
              <span className="text-xs text-white/60 font-bold whitespace-nowrap">
                {doneSteps.size} / {totalSteps} {stepsComplete ? "done ✓" : "steps"}
              </span>
            </div>

            <h2 className="font-display text-xl font-extrabold mt-7">Checklist</h2>
            <ol className="mt-3 space-y-2.5">
              {challenge.steps.map((s, i) => {
                const checked = completed || doneSteps.has(i);
                return (
                  <li key={`c-step-${i}`}
                      data-testid={`challenge-step-${i}`}
                      className={`flex items-start gap-3 p-3 rounded-2xl border transition ${
                        checked ? "bg-neongreen/5 border-neongreen/25" : "bg-white/5 border-white/10 hover:bg-white/8"
                      }`}>
                    <Checkbox
                      checked={checked}
                      disabled={completed || busyStep !== null}
                      onCheckedChange={(v) => toggleStep(i, !!v)}
                      data-testid={`challenge-step-check-${i}`}
                      className="size-6 rounded-full mt-0.5 flex-shrink-0 data-[state=checked]:bg-neongreen data-[state=checked]:border-neongreen border-neoncyan/60"
                    />
                    <div className={`flex-1 leading-relaxed transition ${checked ? "text-white/45 line-through" : "text-white/85"}`}>
                      <span className="text-xs text-neoncyan font-bold mr-2">{i + 1}.</span>{s}
                    </div>
                  </li>
                );
              })}
            </ol>
          </>
        )}

        {challenge.links?.length > 0 && (
          <>
            <h2 className="font-display text-xl font-extrabold mt-8">Trusted links</h2>
            <div className="space-y-2 mt-3">
              {challenge.links.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                   data-testid={`challenge-link-${l.url}`}
                   className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition">
                  <span className="font-semibold text-sm">{l.label}</span>
                  <ExternalLink size={14} className="text-white/50"/>
                </a>
              ))}
            </div>
          </>
        )}

        {joined && (
          <>
            <h2 className="font-display text-xl font-extrabold mt-8 flex items-center gap-2">
              <Sparkles size={18} className="text-neonyellow"/> Prove it
            </h2>
            <p className="text-sm text-white/60 mt-1">
              {challenge.proof_prompt || "Upload a photo, PDF or short video showing you completed the challenge."}
            </p>

            <div className="mt-4 rounded-2xl border-2 border-dashed border-white/15 p-5 bg-white/[0.03]">
              {!completed && (
                <>
                  <label className="text-xs font-bold text-white/60 uppercase tracking-widest">Caption (optional)</label>
                  <input
                    type="text"
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder="e.g. First month budgeted!"
                    maxLength={500}
                    disabled={uploading}
                    data-testid="proof-caption-input"
                    className="mt-1 w-full rounded-xl bg-white/8 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-neoncyan"
                  />
                </>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,application/pdf"
                onChange={(e) => uploadProof(e.target.files?.[0])}
                disabled={uploading}
                data-testid="proof-file-input"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="proof-upload-button"
                className={`mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold text-sm transition ${
                  uploading
                    ? "bg-white/10 text-white/40 cursor-not-allowed"
                    : "btn-primary-neon"
                }`}>
                {uploading ? (
                  <><Loader2 size={16} className="animate-spin"/> Uploading… {progress}%</>
                ) : (
                  <><Upload size={16}/> {submissions.length > 0 ? "Upload another" : "Upload proof"}</>
                )}
              </button>
              {uploading && (
                <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="proof-upload-progress-bar">
                  <div className="h-full bg-neoncyan transition-all" style={{ width: `${progress}%` }}/>
                </div>
              )}
              <p className="text-xs text-white/40 mt-2">
                Images, PDFs or short videos · max 50MB. Uploads are private to you and the S0rted admins.
              </p>
            </div>

            {submissions.length > 0 && (
              <>
                <h3 className="font-display text-lg font-extrabold mt-6">Your submissions</h3>
                <div className="space-y-2.5 mt-3">
                  {submissions.map((s) => (
                    <SubmissionRow
                      key={s.submission_id}
                      challengeId={id}
                      submission={s}
                      onDelete={() => deleteSubmission(s.submission_id)}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {completed && (
          <div className="flex items-center gap-4 mt-8 p-5 rounded-2xl bg-neongreen/5 border border-neongreen/25">
            <div className="size-10 rounded-full flex items-center justify-center bg-neongreen/20 text-neongreen">
              <CheckCircle2 size={20}/>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-neongreen">Challenge complete!</div>
              <div className="text-xs text-white/60">
                You earned +{challenge.reward_points} XP. You can still upload additional proof.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SubmissionRow({ challengeId, submission, onDelete }) {
  const isImage = submission.mime_type?.startsWith("image/");
  const isVideo = submission.mime_type?.startsWith("video/");
  const isPdf = submission.mime_type === "application/pdf";
  const proofUrl = `${API}/challenges/${challengeId}/proof/${submission.submission_id}`;

  const Icon = isImage ? ImageIcon : isVideo ? VideoIcon : FileText;

  const sizeKb = Math.round((submission.size_bytes || 0) / 1024);
  const sizeText = sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`;

  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-white/5 border border-white/10"
         data-testid={`submission-${submission.submission_id}`}>
      <div className="w-14 h-14 rounded-xl overflow-hidden bg-black/30 flex items-center justify-center flex-shrink-0">
        {isImage ? (
          <img src={proofUrl} alt={submission.file_name}
               className="w-full h-full object-cover"
               data-testid={`submission-thumb-${submission.submission_id}`}/>
        ) : isVideo ? (
          <video src={proofUrl} className="w-full h-full object-cover" muted playsInline preload="metadata"/>
        ) : (
          <Icon className="text-neoncyan" size={22}/>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{submission.file_name}</div>
        <div className="text-xs text-white/50">{sizeText} · {isPdf ? "PDF" : isVideo ? "Video" : isImage ? "Image" : "File"}</div>
        {submission.caption && (
          <div className="text-xs text-white/70 mt-1 italic truncate">&ldquo;{submission.caption}&rdquo;</div>
        )}
      </div>
      <a href={proofUrl} target="_blank" rel="noopener noreferrer"
         data-testid={`submission-view-${submission.submission_id}`}
         className="text-xs text-neoncyan hover:text-neonyellow px-2 py-1 rounded-lg hover:bg-white/5 transition">
        View
      </a>
      <button onClick={onDelete}
              data-testid={`submission-delete-${submission.submission_id}`}
              className="text-white/40 hover:text-neonpink p-2 rounded-lg hover:bg-white/5 transition">
        <Trash2 size={14}/>
      </button>
    </div>
  );
}
